import React, { useMemo, useState } from "react";
import { getIdentifier } from "../../../utils";
import { useAttributeSelected } from "../AttributeSelectedProvider/AttributeSelectedProvider";
import AttributeValue from "../AttributeValue/AttributeValue";
import GraphProvider from "../GraphProvider/GraphProvider";
import NodePath from "../NodePath/NodePath";
import styles from "./TimesheetInvestigationCard.module.scss";

export interface TimesheetInvestigationColumn {
  id: string;
  header: string;
  attributeId: string;
}

export interface TimesheetInvestigationTotal {
  id: string;
  header: string;
  attributeId: string;
}

export interface TimesheetInvestigationConfig {
  positionRelationship?: string;
  timeEntryRelationship?: string;
  columns?: TimesheetInvestigationColumn[];
  totals?: TimesheetInvestigationTotal[];
}

export interface TimesheetInvestigationCardProps {
  graph: any;
  config?: TimesheetInvestigationConfig;
  className?: string;
}

type TimeEntryRow = {
  path: string;
  label: string;
  nodes: Record<string, any>;
};

type PositionGroup = {
  path: string;
  label: string;
  rowsByPath: Map<string, TimeEntryRow>;
  totalNodes: Record<string, any>;
};

const emptyList: never[] = [];

const getNodeAttributeCandidates = (node: any) => {
  const candidates = [node?.id, node?.attribute, node?.v, node?.path].filter(Boolean).map(String);
  if (typeof node?.path === "string") {
    const parts = node.path.split("/");
    const last = parts[parts.length - 1];
    if (last) candidates.push(last);
  }
  return candidates;
};

const nodeMatchesAttribute = (node: any, attributeId: string) =>
  getNodeAttributeCandidates(node).some(
    (candidate) => candidate === attributeId || candidate.endsWith(`/${attributeId}`),
  );

const findAncestorPathForRelationship = (path: string | undefined, relationshipName: string | undefined) => {
  if (!path || !relationshipName) return null;
  const parts = path.split("/");
  for (let index = parts.length - 2; index >= 0; index -= 1) {
    if (parts[index] === relationshipName && parts[index + 1]) {
      return parts.slice(0, index + 2).join("/");
    }
  }
  return null;
};

const getTimeEntryLabel = (timeEntryPath: string) => timeEntryPath.split("/").pop() || timeEntryPath;

const getPositionLabel = (positionPath: string, graph: any) => getIdentifier(positionPath, graph) || positionPath;

const compareRows = (left: TimeEntryRow, right: TimeEntryRow) =>
  left.label.localeCompare(right.label, undefined, { numeric: true });

const getParentPath = (path: string | undefined) => {
  if (!path) return null;
  const parts = path.split("/");
  if (parts.length <= 1) return null;
  return parts.slice(0, -1).join("/");
};

const isDirectAttributeOfPath = (node: any, parentPath: string) => getParentPath(node?.path || node?.id) === parentPath;

const getOrCreatePositionGroup = (groups: Map<string, PositionGroup>, positionPath: string, graph: any) => {
  let group = groups.get(positionPath);
  if (!group) {
    group = {
      path: positionPath,
      label: getPositionLabel(positionPath, graph),
      rowsByPath: new Map(),
      totalNodes: {},
    };
    groups.set(positionPath, group);
  }
  return group;
};

const buildPositionGroups = (graph: any, config: TimesheetInvestigationConfig) => {
  const columns = config.columns || emptyList;
  const totals = config.totals || emptyList;
  const columnAttributeIds = columns.map((item) => item.attributeId).filter(Boolean);
  const totalAttributeIds = totals.map((item) => item.attributeId).filter(Boolean);
  if (!graph?.nodes || !graph?.node || (columnAttributeIds.length === 0 && totalAttributeIds.length === 0)) return [];

  const groups = new Map<string, PositionGroup>();

  for (const nodeId of graph.nodes()) {
    const node = graph.node(nodeId);
    if (!node) continue;

    const positionPath = findAncestorPathForRelationship(node.path || node.id, config.positionRelationship);
    if (!positionPath) continue;

    const matchedTotalAttributeId = totalAttributeIds.find((attributeId) => nodeMatchesAttribute(node, attributeId));
    if (matchedTotalAttributeId && isDirectAttributeOfPath(node, positionPath)) {
      const group = getOrCreatePositionGroup(groups, positionPath, graph);
      group.totalNodes[matchedTotalAttributeId] = node;
    }

    const matchedColumnAttributeId = columnAttributeIds.find((attributeId) => nodeMatchesAttribute(node, attributeId));
    if (!matchedColumnAttributeId) continue;

    const timeEntryPath = findAncestorPathForRelationship(node.path || node.id, config.timeEntryRelationship);
    if (!timeEntryPath) continue;

    const group = getOrCreatePositionGroup(groups, positionPath, graph);
    let row = group.rowsByPath.get(timeEntryPath);
    if (!row) {
      row = {
        path: timeEntryPath,
        label: getTimeEntryLabel(timeEntryPath),
        nodes: {},
      };
      group.rowsByPath.set(timeEntryPath, row);
    }

    row.nodes[matchedColumnAttributeId] = node;
  }

  return Array.from(groups.values()).sort((left, right) =>
    left.label.localeCompare(right.label, undefined, { numeric: true }),
  );
};

const TimesheetInvestigationCard = ({ graph, config = {}, className }: TimesheetInvestigationCardProps) => {
  const columns = config.columns || emptyList;
  const totals = config.totals || emptyList;
  const groups = useMemo(() => buildPositionGroups(graph, config), [graph, config]);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const selected = useAttributeSelected();
  const selectedGroup = groups.find((group) => group.path === selectedPath) || groups[0];
  const rows = selectedGroup ? Array.from(selectedGroup.rowsByPath.values()).sort(compareRows) : [];

  if (!config.positionRelationship || !config.timeEntryRelationship) {
    return <div className={styles.empty}>Choose a position object and time entry object.</div>;
  }

  if (columns.length === 0 && totals.length === 0) {
    return <div className={styles.empty}>Add columns or totals to display timesheet data.</div>;
  }

  if (!selectedGroup) {
    return <div className={styles.empty}>No matching time entries found.</div>;
  }

  return (
    <GraphProvider graph={graph}>
      <div className={className ? `${styles.root} ${className}` : styles.root}>
        <div className={styles.tabs}>
          {groups.map((group) => (
            <button
              key={group.path}
              type="button"
              className={styles.tab}
              data-selected={group.path === selectedGroup.path ? "true" : undefined}
              onClick={() => setSelectedPath(group.path)}
            >
              {group.label}
            </button>
          ))}
        </div>
        <div className={styles.positionPath}>
          <NodePath
            node={{ path: selectedGroup.path, description: selectedGroup.label }}
            showDescription
          />
        </div>
        <div className={styles.body}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.id || column.attributeId}>{column.header || column.attributeId}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.path}>
                    {columns.map((column) => {
                      const node = row.nodes[column.attributeId];
                      return (
                        <td key={column.id || column.attributeId}>
                          {node ? (
                            <button
                              type="button"
                              className={styles.attributeValueButton}
                              onClick={() => selected?.onSelectAttribute?.(node)}
                            >
                              <AttributeValue node={node} />
                            </button>
                          ) : (
                            <span className={styles.missing}>-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totals.length > 0 ? (
            <aside className={styles.totals}>
              {totals.map((total) => {
                const node = selectedGroup.totalNodes[total.attributeId];
                return (
                  <div
                    key={total.id || total.attributeId}
                    className={styles.total}
                  >
                    <span className={styles.totalLabel}>{total.header || total.attributeId}</span>
                    {node ? (
                      <button
                        type="button"
                        className={styles.totalValue}
                        onClick={() => selected?.onSelectAttribute?.(node)}
                      >
                        <AttributeValue node={node} />
                      </button>
                    ) : (
                      <span className={styles.totalValue}>-</span>
                    )}
                  </div>
                );
              })}
            </aside>
          ) : null}
        </div>
      </div>
    </GraphProvider>
  );
};

export default TimesheetInvestigationCard;
